import axios, { AxiosInstance } from "axios";

import C from "@constants/constants";

import userService from "@services/user.service";

interface IHttpService {
  http: AxiosInstance | null;
  httpFileUpload: AxiosInstance | null;
}

const Http: IHttpService = {
  http: null,
  httpFileUpload: null,
};

/**
 * Configure http requests..
 */
const set = async () => {
  setHttp();
  await setFileHttp();
};

export const setupRefreshTokenInterceptor = (axiosInstance: AxiosInstance): void => {
  // Add a response interceptor
  axiosInstance.interceptors.response.use(
    (response) => response, // If the response is successful just return it
    async (error) => {
      const maxRetries = 3;
      let retryCount = 0;
      // If 401, try to refresh token
      if (error.response && error.response.status === 401) {
        while (retryCount <= maxRetries) {
          try {
            const response = await axios.post(
              `${C.VITE_APP_EXPRESS_SERVER}/1/user/refresh-auth-token`,
              {},
              {
                withCredentials: true, // to allow cookies
              },
            );
            const newAuthToken = response.data;
            localStorage.setItem(C.AUTH_TOKEN_KEY, newAuthToken);
            axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${newAuthToken}`;
            const originalRequest = error.config;
            originalRequest.headers["Authorization"] = `Bearer ${newAuthToken}`;
            // Original request successful, get out of the loop
            return axiosInstance(originalRequest);
          } catch (error_: unknown) {
            retryCount++;

            // Type guard to check if error_ is an AxiosError
            if (axios.isAxiosError(error_) && error_.response?.status === 401) {
              userService.clearUser();
              break;
            }

            if (retryCount > maxRetries) {
              userService.clearUser();
              break;
            }
          }
        }
      }
      // If the response status is not 401, just reject the promise.
      throw error;
    },
  );
};

/**
 * Set up axios instance for general HTTP requests.
 */
const setHttp = () => {
  const authToken = localStorage.getItem(C.AUTH_TOKEN_KEY);

  Http.http = axios.create({
    baseURL: C.VITE_APP_EXPRESS_SERVER,
    headers: {
      authorization: `Bearer ${authToken}`,
    },
    withCredentials: true,
  });

  setupRefreshTokenInterceptor(Http.http);
};

/**
 * Set up axios instance for general HTTP with at attached file.
 */
const setFileHttp = () => {
  const authToken = localStorage.getItem(C.AUTH_TOKEN_KEY);

  Http.httpFileUpload = axios.create({
    baseURL: C.VITE_APP_EXPRESS_SERVER,
    headers: {
      authorization: `Bearer ${authToken}`,
      "Content-Type": "multipart/form-data",
    },
    withCredentials: true,
  });

  setupRefreshTokenInterceptor(Http.httpFileUpload);
};

/**
 * Get axios instance for general HTTP requests.
 * @returns - Axios http instance.
 */
const get = () => Http.http;
const file = () => Http.httpFileUpload;

/**
 * Calculates the size in kilobytes of a given payload.
 * @param payload - The payload to calculate the size of
 * @returns The size of the payload in kilobytes, or undefined if calculation fails
 */
const getPayloadSizeInKB = <T>(payload: T): number | undefined => {
  try {
    const jsonString = JSON.stringify(payload);
    const sizeInBytes = new TextEncoder().encode(jsonString).length;
    return sizeInBytes / 1024;
  } catch {
    return undefined;
  }
};

/**
 * Splits an array into smaller chunks based on a maximum size in kilobytes.
 * @param array - The array to be chunked
 * @param maxSizeInKB - The maximum size of each chunk in kilobytes
 * @returns An array of smaller chunks
 */
const chunkArrayInKB = <T>(array: T[], maxSizeInKB: number = C.MAX_REQUEST_BODY_SIZE_KB): T[][] => {
  const chunks: T[][] = [];
  let chunk: T[] = [];

  for (const item of array) {
    chunk.push(item);
    const chunkSize = getPayloadSizeInKB(chunk);
    if (chunkSize !== undefined && chunkSize > maxSizeInKB) {
      chunk.pop();
      chunks.push(chunk);
      chunk = [item];
    }
  }

  if (chunk.length > 0) {
    chunks.push(chunk);
  }

  return chunks;
};

export default {
  ...Http,
  chunkArrayInKB,
  file,
  get,
  getPayloadSizeInKB,
  set,
  setFileHttp,
  setHttp,
};
